import {
  CircleExclamation,
  CircleInfo,
  Plus,
  TrashBin,
} from "@gravity-ui/icons";
import {
  Button,
  Checkbox,
  Icon,
  Select,
  Text,
  TextInput,
  Tooltip,
} from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import {
  AvitoFieldInfoDto,
  ExportFormats,
  ParamMappingDtoOnMissEnum,
} from "definitions/exporter";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { T } from "src/i18n";
import {
  fetchProductSelectionParamValues,
  getExportOptionsRequest,
  getProductSelectionParamKeysRequest,
} from "src/pages/ExportPage/store/fetchers";
import { pluralNumber } from "src/util";
import { ShowConfirmDialog } from "src/widgets/Alert/Alert";

import { ExportTaskFormValues } from "../../ExportTasksForm.types";
import css from "./MappingForm.module.scss";

const ON_MISS_LABELS: Record<ParamMappingDtoOnMissEnum, string> = {
  raw: "Оставить как есть",
  skip: "Пропустить",
  default: "Подставить значение",
  error: "Завершить с ошибкой",
};

const onMissOptions = Object.entries(ON_MISS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const NO_SAMPLE_HINT =
  "Сначала выберите выборку товаров в секции «Выборка товаров» — иначе ключи параметров будут недоступны для выбора.";

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];

interface ValueMapEditorProps {
  fieldTag: string;
  enumValues: string[];
  availableRawValues: string[];
  isLoadingRawValues: boolean;
  /** Хоть один paramKey выбран в форме. Меняет UX в "нет данных" кейсе. */
  hasParamKeys: boolean;
  /** Выборка выбрана. Без неё подсказок не будет в принципе. */
  hasSelection: boolean;
}

const ValueMapEditor = ({
  fieldTag,
  enumValues,
  availableRawValues,
  isLoadingRawValues,
  hasParamKeys,
  hasSelection,
}: ValueMapEditorProps) => {
  const { watch, setValue } = useFormContext<ExportTaskFormValues>();
  const valueMapPath = `mapping.params.${fieldTag}.valueMap` as const;

  const valueMap = watch(valueMapPath);
  // Стабилизируем сигнатуру: watch() возвращает свежую object-reference на
  // каждый ререндер формы, без сигнатуры useEffect ниже срабатывал бы на
  // любой keystroke в произвольном поле формы.
  const valueMapSignature = JSON.stringify(valueMap ?? {});

  const [entries, setEntries] = useState<[string, string][]>(() =>
    valueMap && typeof valueMap === "object" ? Object.entries(valueMap) : [],
  );

  // Пересинхронизируем локальные entries только когда содержимое valueMap
  // действительно изменилось извне (reset задачи). Сравниваем по сигнатуре, а
  // не по object-reference, иначе любой ререндер формы сбросит editing-state.
  const [lastWrittenSignature, setLastWrittenSignature] =
    useState(valueMapSignature);
  if (valueMapSignature !== lastWrittenSignature) {
    setLastWrittenSignature(valueMapSignature);
    setEntries(
      valueMap && typeof valueMap === "object" ? Object.entries(valueMap) : [],
    );
  }

  const writeEntries = (next: [string, string][]) => {
    const obj: Record<string, string> = {};
    for (const [k, v] of next) {
      if (k.trim().length === 0) continue;
      obj[k] = v;
    }
    setLastWrittenSignature(JSON.stringify(obj));
    setValue(valueMapPath, obj, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  // Все мутации через функциональный setState — иначе быстрые повторные клики
  // (Add×2, Remove во время open-popup) видят stale closure entries и теряют изменения.
  const updateEntry = (index: number, raw: string, canonical: string) => {
    setEntries((prev) => {
      const next = prev.map((e, i): [string, string] =>
        i === index ? [raw, canonical] : e,
      );
      writeEntries(next);
      return next;
    });
  };

  const removeEntry = (index: number) => {
    setEntries((prev) => {
      const next = prev.filter((_, i) => i !== index);
      writeEntries(next);
      return next;
    });
  };

  const addEntry = () => {
    // canonical = "" чтобы юзер сознательно выбрал значение из enumValues, а не
    // получил автоматически подставленный первый вариант.
    setEntries((prev) => [...prev, ["", ""]]);
    // Не пишем в форму — пустой raw-ключ всё равно отфильтруется в writeEntries.
  };

  // Избыточное правило: raw уже совпадает с canonical, преобразование тождественное.
  // Бэк всё равно вернёт то же значение — правило только засоряет конфиг и UI.
  const isRedundant = (raw: string, canonical: string) =>
    !!raw && !!canonical && raw === canonical;

  const redundantCount = entries.filter(([raw, canonical]) =>
    isRedundant(raw, canonical),
  ).length;

  const clearRedundant = () => {
    setEntries((prev) => {
      const next = prev.filter(
        ([raw, canonical]) => !isRedundant(raw, canonical),
      );
      writeEntries(next);
      return next;
    });
  };

  const handleClearRedundant = async () => {
    const word = pluralNumber(redundantCount, [
      "избыточное правило",
      "избыточных правила",
      "избыточных правил",
    ]);
    const result = await ShowConfirmDialog(
      `Удалить ${redundantCount} ${word}? Правила, где raw совпадает с каноническим значением, не влияют на результат.`,
      "Очистить избыточные правила",
    );
    if (result) clearRedundant();
  };

  return (
    <div className={css.valueMap}>
      <div className={css.valueMapHeader}>
        <Text variant="body-1" color="secondary">
          Сопоставление значений (raw → каноническое)
        </Text>
        <div className={css.valueMapHeaderActions}>
          {redundantCount > 0 && (
            <Button view="flat" size="s" onClick={handleClearRedundant}>
              <Icon data={TrashBin} size={14} />
              <T
                keyName="export.mapping.valueMap.clearRedundant"
                defaultValue="Очистить избыточные ({count})"
                params={{ count: redundantCount }}
              />
            </Button>
          )}
          <Button view="outlined" size="s" onClick={addEntry}>
            <Icon data={Plus} size={14} />
            <T
              keyName="export.mapping.valueMap.addRule"
              defaultValue="Добавить"
            />
          </Button>
        </div>
      </div>

      {entries.length === 0 && (
        <Text variant="caption-1" color="secondary">
          Нет правил. Сырое значение из товара передаётся как есть, либо
          применяется стратегия «Если значение не найдено».
        </Text>
      )}

      {/* Если в товарах не нашлось ни одного значения по ключам, а правила
          сохранены — все они мёртвые, ни одно не сматчится. Один общий месседж
          лучше, чем спамить иконкой каждую строку. */}
      {!isLoadingRawValues &&
        hasSelection &&
        hasParamKeys &&
        availableRawValues.length === 0 &&
        entries.some(([raw]) => raw) && (
          <div className={css.valueMapAllStaleAlert}>
            В товарах выборки нет значений по выбранным ключам — ни одно правило
            не сработает.
          </div>
        )}

      {entries.map(([raw, canonical], index) => {
        // Сохранённый raw оставляем выбираемым, даже если его нет в текущей
        // выборке (загрузка задачи из сохранённого state или смена выборки).
        const rawOptions = (() => {
          const set = new Set(availableRawValues);
          if (raw) set.add(raw);
          return Array.from(set).sort((a, b) => a.localeCompare(b, "ru"));
        })();
        // Stale: правило с raw, которого нет в товарах, и при этом сами товары
        // не пусты — то есть мы точно знаем, что это правило никогда не сматчит.
        // Если availableRawValues пустой — отдельный section-level alert выше.
        const isStale =
          !isLoadingRawValues &&
          !!raw &&
          availableRawValues.length > 0 &&
          !availableRawValues.includes(raw);
        const redundant = !isStale && isRedundant(raw, canonical);
        const rawPlaceholder = isLoadingRawValues
          ? "Загружаем значения…"
          : !hasSelection
            ? "Сначала выберите выборку"
            : !hasParamKeys
              ? "Сначала выберите ключи параметров"
              : availableRawValues.length === 0
                ? "В товарах нет значений по этим ключам"
                : "Значение в товаре";
        return (
          <div key={index} className={css.valueMapRow}>
            {isStale ? (
              <Tooltip
                content={`Значение «${raw}» не встречается в товарах текущей выборки — правило не сработает`}
                openDelay={200}
              >
                <span className={css.staleIcon}>
                  <Icon data={CircleExclamation} size={14} />
                </span>
              </Tooltip>
            ) : redundant ? (
              <Tooltip
                content="Правило избыточно: raw совпадает с каноническим значением. Можно удалить."
                openDelay={200}
              >
                <span className={css.redundantIcon}>
                  <Icon data={CircleInfo} size={14} />
                </span>
              </Tooltip>
            ) : (
              <span className={css.staleIconSpacer} />
            )}
            <Select
              size="s"
              filterable
              hasClear
              value={raw ? [raw] : []}
              onUpdate={(v) => updateEntry(index, v[0] ?? "", canonical)}
              width="max"
              placeholder={rawPlaceholder}
            >
              {rawOptions.map((v) => (
                <Select.Option key={v} value={v}>
                  {v}
                </Select.Option>
              ))}
            </Select>
            <span className={css.arrow}>→</span>
            <Select
              size="s"
              value={canonical ? [canonical] : []}
              onUpdate={(v) => updateEntry(index, raw, v[0] ?? "")}
              width="max"
              placeholder="Канон. значение"
            >
              {enumValues.map((v) => (
                <Select.Option key={v} value={v}>
                  {v}
                </Select.Option>
              ))}
            </Select>
            <Button
              view="flat"
              size="s"
              onClick={() => removeEntry(index)}
              title="Удалить правило"
            >
              <Icon data={TrashBin} size={14} />
            </Button>
          </div>
        );
      })}
    </div>
  );
};

interface MappingFieldRowProps {
  tplField: AvitoFieldInfoDto;
  availableParamKeys: string[];
  isLoadingParamKeys: boolean;
}

const MappingFieldRow = ({
  tplField,
  availableParamKeys,
  isLoadingParamKeys,
}: MappingFieldRowProps) => {
  const { control, watch } = useFormContext<ExportTaskFormValues>();

  const paramsPath = `mapping.params.${tplField.tag}.paramKeys` as const;
  const onMissPath = `mapping.params.${tplField.tag}.onMiss` as const;
  const defaultPath = `mapping.params.${tplField.tag}.defaultValue` as const;
  const caseSensitivePath =
    `mapping.params.${tplField.tag}.caseSensitive` as const;
  const applyDefaultOnSourceMissPath =
    `mapping.params.${tplField.tag}.applyDefaultOnSourceMiss` as const;

  const selectionId = watch("selectProductSampleId");
  const caseSensitive = !!watch(caseSensitivePath);
  const onMissValue = watch(onMissPath);
  const enumValues = tplField.enumValues;
  const isEnum =
    tplField.kind === "enum" &&
    Array.isArray(enumValues) &&
    enumValues.length > 0;

  // Не мемоизируем: useMemo с deps [watch, paramsPath] (обе стабильные строки)
  // не пересчитается на ре-рендере → значение зависнет.
  const selectedParamKeys = toStringArray(watch(paramsPath));

  // Селектед-значения добавляем поверх availableParamKeys, чтобы при загрузке
  // задачи с ключом, которого больше нет в актуальной выборке, он отобразился.
  // Стабилизируем deps через JSON.stringify — selectedParamKeys пересоздаётся
  // на каждый render, поэтому object-reference-сравнение в useMemo бессмысленно.
  const selectedKeysSignature = JSON.stringify(selectedParamKeys);
  // Простой union: при смене выборки availableParamKeys сменится → старые
  // ключи естественно выпадут. Сохранённые в форме ключи всегда видны (даже
  // если их больше нет в текущей выборке), чтобы юзер мог их удалить.
  const paramKeyOptions = useMemo(
    () =>
      Array.from(new Set([...availableParamKeys, ...selectedParamKeys])).sort(
        (a, b) => a.localeCompare(b, "ru"),
      ),
    // selectedParamKeys пересоздаётся каждый render — сравниваем по сигнатуре.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableParamKeys, selectedKeysSignature],
  );

  // Фетчим уникальные raw-значения для дропдауна слева в valueMap.
  // Реактивен на (selectionId, выбранные paramKeys, caseSensitive).
  // selectedParamKeys exclude из deps — массив пересоздаётся на каждый
  // watch()-render. Сигнатура — стабильная строка, восстанавливаем JSON.parse
  // внутри. Cleanup-функция abort'ит in-flight запрос при unmount/перезапросе.
  const [rawValues, setRawValues] = useState<string[]>([]);
  const [isLoadingRawValues, setIsLoadingRawValues] = useState(false);
  useEffect(() => {
    const keys: string[] = JSON.parse(selectedKeysSignature);
    if (!selectionId || keys.length === 0) {
      setRawValues([]);
      setIsLoadingRawValues(false);
      return;
    }
    const controller = new AbortController();
    setIsLoadingRawValues(true);
    fetchProductSelectionParamValues({
      selectionId,
      key: keys,
      caseSensitive,
      limit: 1000,
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        setRawValues(data.values ?? []);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.name === "CanceledError") return;
        setRawValues([]);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setIsLoadingRawValues(false);
      });
    return () => controller.abort();
  }, [selectionId, selectedKeysSignature, caseSensitive]);

  return (
    <div className={css.row}>
      <div className={css.rowHeader}>
        <Text variant="subheader-2">{tplField.tag}</Text>
        {tplField.required ? (
          <span className={css.requiredBadge}>
            <T
              keyName="export.mapping.field.required"
              defaultValue="обязательное"
            />
          </span>
        ) : (
          <span className={css.optionalBadge}>
            <T
              keyName="export.mapping.field.optional"
              defaultValue="опционально"
            />
          </span>
        )}
        {isEnum && enumValues && (
          <Text variant="caption-1" color="secondary">
            enum · {enumValues.length}{" "}
            {enumValues.length === 1 ? "значение" : "значений"}
          </Text>
        )}
      </div>

      <div className={css.field}>
        <Text variant="body-1" color="secondary">
          Ключи параметров товара
        </Text>
        <Controller
          name={paramsPath}
          control={control}
          rules={
            tplField.required
              ? {
                  validate: (v) =>
                    (Array.isArray(v) && v.length > 0) ||
                    "Выберите хотя бы один ключ",
                }
              : undefined
          }
          render={({ field: rhfField, fieldState }) => (
            <Select
              multiple
              filterable
              hasClear
              value={toStringArray(rhfField.value)}
              onUpdate={(v) => rhfField.onChange(v)}
              placeholder={
                isLoadingParamKeys
                  ? "Загружаем доступные ключи…"
                  : paramKeyOptions.length === 0
                    ? "Выберите выборку, чтобы увидеть доступные ключи"
                    : "Выберите ключи"
              }
              disabled={
                isLoadingParamKeys ||
                (availableParamKeys.length === 0 &&
                  selectedParamKeys.length === 0)
              }
              validationState={fieldState.error ? "invalid" : undefined}
              errorMessage={fieldState.error?.message}
              width="max"
              size="m"
            >
              {paramKeyOptions.map((k) => (
                <Select.Option key={k} value={k}>
                  {k}
                </Select.Option>
              ))}
            </Select>
          )}
        />
      </div>

      <div className={css.subRow}>
        <div className={css.field}>
          <Text variant="body-1" color="secondary">
            Если значение из товара не подошло
          </Text>
          <Controller
            name={onMissPath}
            control={control}
            render={({ field: rhfField }) => (
              <Select
                value={[rhfField.value ?? "raw"]}
                onUpdate={(value) => rhfField.onChange(value[0])}
                width="max"
                size="m"
              >
                {onMissOptions.map((o) => (
                  <Select.Option key={o.value} value={o.value}>
                    {o.label}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </div>

        {onMissValue === "default" && (
          <div className={css.field}>
            <Text variant="body-1" color="secondary">
              Значение по умолчанию
            </Text>
            <Controller
              name={defaultPath}
              control={control}
              shouldUnregister
              render={({ field: rhfField }) => (
                <TextInput
                  value={rhfField.value ?? ""}
                  onChange={(e) => rhfField.onChange(e.target.value)}
                  placeholder="Подставится при отсутствии"
                  size="m"
                />
              )}
            />
          </div>
        )}
      </div>

      {onMissValue === "default" && (
        <Controller
          name={applyDefaultOnSourceMissPath}
          control={control}
          shouldUnregister
          render={({ field: rhfField }) => (
            <Checkbox
              checked={!!rhfField.value}
              onUpdate={(v) => rhfField.onChange(v)}
              size="m"
            >
              Подставлять также при отсутствии параметра в товаре
            </Checkbox>
          )}
        />
      )}

      <Controller
        name={caseSensitivePath}
        control={control}
        render={({ field: rhfField }) => (
          <Checkbox
            checked={!!rhfField.value}
            onUpdate={(v) => rhfField.onChange(v)}
            size="m"
          >
            Учитывать регистр при поиске ключа в параметрах товара
          </Checkbox>
        )}
      />

      {isEnum && enumValues && (
        <ValueMapEditor
          fieldTag={tplField.tag}
          enumValues={enumValues}
          availableRawValues={rawValues}
          isLoadingRawValues={isLoadingRawValues}
          hasParamKeys={selectedParamKeys.length > 0}
          hasSelection={!!selectionId}
        />
      )}
    </div>
  );
};

export const MappingForm = () => {
  const { watch } = useFormContext<ExportTaskFormValues>();

  const [exportOptions] = useAtom(getExportOptionsRequest.dataAtom);
  const [paramKeysData] = useAtom(getProductSelectionParamKeysRequest.dataAtom);
  const [isLoadingParamKeys] = useAtom(
    getProductSelectionParamKeysRequest.isLoadingAtom,
  );
  const fetchParamKeys = useAction(getProductSelectionParamKeysRequest);

  const templateId = watch("avitoOptions.templateId");
  const templateGroups = watch("avitoOptions.templateGroups");
  const selectionId = watch("selectProductSampleId");

  const avitoTemplates = useMemo(
    () =>
      exportOptions.formatsInfo?.find((f) => f.format === ExportFormats.Avito)
        ?.avitoTemplates ?? [],
    [exportOptions.formatsInfo],
  );

  // Маппинг един для всего фида. Auto-режим (фронт по умолчанию): шаблон у
  // каждой категории свой и подбирается бэком — берём объединение полей ВСЕХ
  // шаблонов. multi (API): объединение шаблонов групп; single (API): один
  // templateId.
  const selectedTemplateIds = useMemo(() => {
    if (templateGroups && templateGroups.length > 0) {
      return Array.from(new Set(templateGroups.map((g) => g.templateId)));
    }
    if (templateId !== undefined) return [templateId];
    return avitoTemplates.map((t) => t.templateId);
  }, [templateGroups, templateId, avitoTemplates]);

  useEffect(() => {
    if (selectionId) {
      fetchParamKeys(selectionId);
    }
  }, [selectionId, fetchParamKeys]);

  // Поля для маппинга — объединение mappable-полей всех выбранных шаблонов,
  // дедуп по тегу (первое вхождение): одинаковые теги из разных шаблонов
  // маппятся один раз (структура mapping.params плоская, ключ — тег).
  const templateFields = useMemo(() => {
    const seenTags = new Set<string>();
    const fields: AvitoFieldInfoDto[] = [];
    for (const id of selectedTemplateIds) {
      const template = avitoTemplates.find((t) => t.templateId === id);
      for (const tplField of template?.fields ?? []) {
        if (seenTags.has(tplField.tag)) continue;
        seenTags.add(tplField.tag);
        fields.push(tplField);
      }
    }
    return fields;
  }, [avitoTemplates, selectedTemplateIds]);

  if (selectedTemplateIds.length === 0) {
    return (
      <Text variant="body-1" color="secondary">
        Поля для маппинга появятся после загрузки шаблонов Avito.
      </Text>
    );
  }

  if (templateFields.length === 0) {
    return (
      <Text variant="body-1" color="secondary">
        Для выбранного шаблона нет полей, требующих маппинга.
      </Text>
    );
  }

  return (
    <div className={css.wrapper}>
      <Text variant="body-1" color="secondary" className={css.intro}>
        Сопоставьте поля выбранного шаблона Avito с параметрами товаров. Для
        каждого поля укажите, по каким ключам искать значение, что делать при
        отсутствии и при необходимости — словарь канонических значений.
      </Text>

      {!selectionId && (
        <Text variant="body-1" color="warning">
          {NO_SAMPLE_HINT}
        </Text>
      )}

      {templateFields.map((tplField) => (
        <MappingFieldRow
          key={tplField.tag}
          tplField={tplField}
          availableParamKeys={paramKeysData.keys}
          isLoadingParamKeys={isLoadingParamKeys}
        />
      ))}
    </div>
  );
};
